'use client';

import React, { useEffect, useRef } from 'react';
import { Container, useAutoPlayVideoRef } from '../shared';
import { LinkButton } from './StyleConstants';
import styles from './HeroSection.module.css';
import { getSplitChars } from './AnimatedTextSection';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import gsap from 'gsap';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

const mobileVideoPath = getCompressedAssetUrl(
  '/web_v2/rare/videos/gold-pellets-ring.mp4/w_1920',
);
const desktopVideoPath = getCompressedAssetUrl(
  '/web_v2/rare/videos/hero-rare-1920-2.mp4/w_1920',
);

const Caption = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={`${styles.caption}${className ? ` ${className}` : ''}`}
    {...props}
  />
);

export const HeroSection = () => {
  const videoRef = useAutoPlayVideoRef();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const gsapContext = useGsapContext();
  const searchParams = useSearchParams();
  const t = useTranslations('ring');

  useEffect(() => {
    const setDesktopVideoIfLargeScreen = () => {
      if (!videoRef.current) {
        return;
      }
      if (
        window.innerWidth > window.innerHeight &&
        (!videoRef.current.src ||
          !videoRef.current.src.endsWith(desktopVideoPath))
      ) {
        videoRef.current.src = desktopVideoPath;
        return;
      }
      if (
        window.innerWidth < window.innerHeight &&
        (!videoRef.current.src ||
          !videoRef.current.src.endsWith(mobileVideoPath))
      ) {
        videoRef.current.src = mobileVideoPath;
        return;
      }
    };

    window.addEventListener('resize', setDesktopVideoIfLargeScreen);
    setDesktopVideoIfLargeScreen();

    return () => {
      window.removeEventListener('resize', setDesktopVideoIfLargeScreen);
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!headingRef.current) {
      return;
    }

    gsapContext.add(() => {
      if (!headingRef.current) {
        return;
      }

      const tl = gsap.timeline({
        defaults: {
          duration: 1,
          ease: 'power2.out',
        },
      });

      tl.fromTo(
        headingRef.current?.querySelectorAll('.hero-letters'),
        {
          opacity: 0,
          yPercent: 100,
        },
        {
          opacity: 1,
          yPercent: 0,
          stagger: 0.04,
        },
      );

      tl.fromTo(
        linkRef.current,
        {
          opacity: 0,
          yPercent: 100,
        },
        {
          opacity: 1,
          yPercent: 0,
          duration: 0.2,
        },
      );
    });

    //--- --- -- end
  }, []);

  return (
    <ContainerRoot>
      {}
      <video preload="none" loop autoPlay muted playsInline ref={videoRef}>
        <source src={mobileVideoPath} type="video/mp4" />
      </video>

      <Container className="container">
        <div className="content">
          <Caption>{t('ring.rare.hero.caption')}</Caption>
          <h1 ref={headingRef}>
            <span className="sentence">
              {getSplitChars(t('ring.rare.hero.titleA'), 'hero-letters')}
            </span>
            <span className="sentence">
              {getSplitChars(t('ring.rare.hero.titleB'), 'hero-letters')}
            </span>
          </h1>
          <p className="sub-head">{t('ring.rare.hero.subHead')}</p>
          <div className="cta-container">
            <LinkButton
              ref={linkRef}
              href={{
                pathname: 'rare/buy',
                query: Object.fromEntries(searchParams?.entries() ?? []),
              }}
            >
              {t('ring.rare.buyNow')}
            </LinkButton>
          </div>
        </div>
      </Container>
    </ContainerRoot>
  );
};

const ContainerRoot = ({ children }: { children?: React.ReactNode }) => (
  <section className={styles.containerRoot}>{children}</section>
);
